<template>
    <div v-if="assetKey && assetOperationHistory.length > 0" :style="cssVars">

        <v-dialog v-if="paramsValue" v-model="paramsDialog" scrollable max-width="800px">
            <v-card>
                <v-card-title class="headline grey lighten-2">Parameters</v-card-title>
                <v-card-text class="my-5">
                    <template v-if="isJSON(paramsValue)"><vue-json-pretty :path="'res'" :data="JSON.parse(paramsValue)"></vue-json-pretty></template>
                    <template v-else><vue-json-pretty :path="'res'" :data="paramsValue"></vue-json-pretty></template>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="paramsDialog = false">Done</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-row>
            <v-row no-gutters justify="center">
                <v-col class="px-3">
                    <v-alert v-model="showSuccessAlert" color="green" dark dismissible                >
                        Business transaction successfully created
                    </v-alert>            
                </v-col>
            </v-row>
        </v-row>

        <v-row class="px-5">
            <v-col>
                <h4 class="font-weight-light">Channel</h4>
                <h3 class="text--secondary font-weight-medium">{{ channelName }}</h3>
            </v-col>
            <v-col>
                <h4 class="font-weight-light">Creation Time</h4>
                <h3 class="text--secondary font-weight-medium">{{ toLongFormatWithYear(creationTime) }}</h3>
            </v-col>
            <v-col>
                <h4 class="font-weight-light">Time Since Creation</h4>
                <h3 class="text--secondary font-weight-medium">{{ getFriendlyTimeElapsedFromNow(creationTime)[0]}}&nbsp;{{ getFriendlyTimeElapsedFromNow(creationTime)[1] }}</h3>
            </v-col>
            <v-col>
                <h4 class="font-weight-light">Last Update Time</h4>
                <h3 class="text--secondary font-weight-medium">{{ toLongFormatWithYear(lastUpdateTime) }}</h3>                
            </v-col>
            <v-col>
                <h4 class="font-weight-light">Time Since Last Update</h4>
                <h3 class="text--secondary font-weight-medium">{{ getFriendlyDuration(lastUpdateTime) }}</h3>                
            </v-col>
        </v-row>
        <v-row class="pa-4">
            <v-col cols="7">
                <v-tabs class="" fixed-tabs v-model="assetViewTab" background-color="primary" dark>
                    <v-tab>Timeline</v-tab>
                    <v-tab>Business Transaction</v-tab>
                    <v-tab v-if="assetOperationHistory.length > 0">{{ assetOperationHistory[0].tracked_hash_label }} Tracking</v-tab>
                </v-tabs>            
                <v-tabs-items v-model="assetViewTab">
                    <v-tab-item>
                        <v-list>
                            <template v-for="(record, index) in assetOperationHistory" >
                                <div :key="index">
                                    <v-list-item>
                                        <v-list-item-avatar>
                                            <v-icon v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_READ" class="light-blue" dark>mdi-eye-circle</v-icon>
                                            <v-icon v-if="creationId == record.id" class="teal" dark>mdi-folder-plus</v-icon>
                                            <v-icon v-if="creationId != record.id && record.operation_type == KEY_TRACE_OPERATION_TYPE_WRITE" class="brown" dark>mdi-pencil</v-icon>
                                            <v-icon v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_DELETE" class="blue-grey" dark>mdi-delete</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <div v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_READ" class="text-h6">
                                                Read by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div v-if="creationId == record.id" class="text-h6">
                                                Created by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div v-if="creationId != record.id && record.operation_type == KEY_TRACE_OPERATION_TYPE_WRITE" class="text-h6">
                                                Updated by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_DELETE" class="text-h6">
                                                Deleted by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div>
                                            <span class="caption text--secondary">{{ record.url }}</span>
                                            </div>
                                            <div class="caption text--secondary">{{ toLongFormatWithoutMin(record.created_at) }}</div>                                        
                                        </v-list-item-content>
                                        <v-chip class="mx-2" @click="handleShowParamsDialog(record)" small>Parameters</v-chip>
                                        <v-chip class="mx-2" @click="handleQuote(record, index)" :disabled="quotes.includes(record)" small>Quote</v-chip>
                                    </v-list-item>
                                    <v-row v-if="index != (assetOperationHistory.length - 1)" align="center" class="my-4 px-8">
                                        <v-divider></v-divider>
                                        <v-chip small class="justify-center text--secondary text-body-2" outlined>{{ getFriendlyDuration(record.created_at, assetOperationHistory[index+1].created_at) }}</v-chip>
                                        <v-divider></v-divider>
                                    </v-row>                    
                                </div>
                            </template>
                        </v-list>
                    </v-tab-item>
                    <v-tab-item>
                        <v-btn class="my-10" outlined color="primary" @click="showBusinessTransactionDialog = true">
                            <v-icon>mdi-plus</v-icon>
                            &nbsp;Define a New Business Transaction
                        </v-btn>
                        <template v-for="(bizTxn, index) in bizTxns">
                            <div v-bind:key="index">
                                <BusinessTransaction
                                    v-bind:key="index"
                                    :title="`${bizTxn.txn_defs[0].name} Timeline`"
                                    :milestones="bizTxn.milestones"
                                    :assetHashHistory="assetHashHistory"
                                    :creationInfo="creationInfo"
                                    :contrastTime="bizTxn.contrastTime"                    
                                    size="small" />
                                <v-divider class="my-5"></v-divider>
                            </div>
                        </template>
                        <!-- Dialog for defining a new business transaction -->
                        <v-dialog scrollable v-model="showBusinessTransactionDialog" width="80%" transition="dialog-top-transition">
                            <v-form ref="newBizTxnForm" @submit.prevent="handleSubmit">
                                <v-card>
                                    <v-card-title class="text-h4">Define a Business Transaction</v-card-title>
                                    <v-card-subtitle class="text-h6">{{ assetKey }}&nbsp;&middot;&nbsp;{{ channelName }}&nbsp;&middot;&nbsp;{{ collectionName }}</v-card-subtitle>
                                    <v-card-text>
                                        <v-text-field class="mt-0 mb-8" name="txn_name" @input="newBusinessTxnName = $event" :rules="[rules.required]" label="Business Transaction Name" required ></v-text-field>
                                        <v-row no-gutters>
                                            <v-col cols="5">
                                                <v-timeline dense clipped>
                                                    <template v-for="(record, index) in assetURIHistory" >
                                                        <div v-bind:key="index">
                                                            <v-timeline-item fill-dot color="grey darken-2" small>
                                                                <v-row no-gutters>
                                                                    <v-col cols="11">
                                                                        <v-list-item class="px-0">
                                                                            <v-list-item-content>
                                                                                <div class="text-h6">
                                                                                    {{ record.msp_id }}
                                                                                    <span class="text-subtitle-1 text--secondary">
                                                                                        @ {{ record.remote_geolocation }}
                                                                                        <span>via <a>{{ record.app_name }}</a></span>
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <span class="text-subtitle-2">{{ record.uri }}</span>
                                                                                </div>
                                                                                <div class="caption text--secondary">{{ toLongFormatWithoutMin(record.start_time) }}</div>                                        
                                                                                <div>
                                                                                    <v-list-item-avatar class="mx-0" v-if="hasOperation(record.operations, KEY_TRACE_OPERATION_TYPE_READ)">
                                                                                        <v-icon class="light-blue" dark>mdi-eye-circle</v-icon>
                                                                                    </v-list-item-avatar>
                                                                                    <v-list-item-avatar class="mx-0" v-if="hasOperation(record.operations, KEY_TRACE_OPERATION_TYPE_DELETE)">
                                                                                        <v-icon class="blue-grey" dark>mdi-delete</v-icon>
                                                                                    </v-list-item-avatar>
                                                                                    <v-list-item-avatar class="mx-0" v-if="record.web_txn_id == creationWebTxnId">
                                                                                        <v-icon class="teal" dark>mdi-folder-plus</v-icon>                                            
                                                                                    </v-list-item-avatar>
                                                                                    <v-list-item-avatar class="mx-0" v-else-if="hasOperation(record.operations, KEY_TRACE_OPERATION_TYPE_WRITE)">
                                                                                        <v-icon class="brown" dark>mdi-pencil</v-icon>                                            
                                                                                    </v-list-item-avatar>
                                                                                    <v-list-item-avatar class="mx-0" v-if="(index > 0) && record.tracked_hash_values && record.tracked_hash_values.split(',').length > 1">
                                                                                        <v-icon class="indigo" dark>mdi-pound</v-icon>                                            
                                                                                    </v-list-item-avatar>
                                                                                </div>
                                                                            </v-list-item-content>
                                                                        </v-list-item>
                                                                    </v-col>
                                                                    <v-col cols="1" align-self="center">
                                                                        <v-icon v-if="newBusinessTxnMilestones.includes(record)" size="28" color="grey" @click="handleRemoveMilestone(record, index)">mdi-minus-circle</v-icon>
                                                                        <v-icon v-else size="28" color="#5E35B1" @click="handleAddMilestone(record, index)">mdi-plus-circle</v-icon>
                                                                    </v-col>
                                                                </v-row>
                                                            </v-timeline-item>
                                                        </div>
                                                    </template>
                                                </v-timeline>
                                            </v-col>
                                            <v-col cols="7" class="px-10">
                                                <BusinessTransaction class="mb-5"
                                                    title="Business Transaction Timeline"
                                                    :milestones="newBusinessTxnMilestones"
                                                    :assetHashHistory="assetHashHistory"
                                                    :creationInfo="creationInfo"
                                                    :contrastTime="newBusinessTxnContrastTime"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-divider></v-divider>                                    
                                    <v-card-actions class="py-4">
                                        <v-spacer></v-spacer>
                                        <v-btn color="primary" type="submit" :loading="loading" :disabled="loading || newBusinessTxnMilestones.length < 2">
                                            Save                                        
                                        </v-btn>
                                        <v-btn text @click="showBusinessTransactionDialog = false">
                                            Cancel
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-form>
                        </v-dialog>    
                        <!-- Dialog end -->                    
                    </v-tab-item>
                    <v-tab-item>
                        <v-list v-if="assetHashHistory.length > 0 && assetHashHistory[0].tracked_hash_label && assetHashHistory[0].tracked_hash_value ">
                            <template  v-for="(record, index) in assetHashHistory" >                                                                
                                <div :key="index">
                                    <v-list-item>
                                        <v-list-item-avatar>
                                            <v-icon v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_READ" class="light-blue" dark>mdi-eye-circle</v-icon>
                                            <v-icon v-if="creationId == record.id" class="teal" dark>mdi-folder-plus</v-icon>
                                            <v-icon v-if="creationId != record.id && record.operation_type == KEY_TRACE_OPERATION_TYPE_WRITE" class="brown" dark>mdi-pencil</v-icon>
                                            <v-icon v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_DELETE" class="blue-grey" dark>mdi-delete</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <div v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_READ" class="text-h6">
                                                Read by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div v-if="creationId == record.id" class="text-h6">
                                                Created by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div v-if="creationId != record.id && record.operation_type == KEY_TRACE_OPERATION_TYPE_WRITE" class="text-h6">
                                                Updated by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <div v-if="record.operation_type == KEY_TRACE_OPERATION_TYPE_DELETE" class="text-h6">
                                                Deleted by {{ record.msp_id }}
                                                <span class="text-subtitle-1 text--secondary">
                                                    @ {{ record.remote_geolocation }}
                                                    <span>via <a>{{ record.app_name }}</a></span>
                                                </span>
                                            </div>
                                            <span class="caption text--secondary">{{ record.url }}</span>
                                            <div class="caption text--secondary">{{ toLongFormatWithoutMin(record.created_at) }}</div>         
                                                <vue-json-compare v-if="index != (assetHashHistory.length - 1)" :oldData="JSON.parse(assetHashHistory[index+1].value_content)" :newData="JSON.parse(record.value_content)"></vue-json-compare>
                                            <div class="text-right"><v-chip small outlined color="primary">{{ record.tracked_hash_value }}</v-chip></div>
                                        </v-list-item-content>
                                        <v-chip class="mx-2" @click="handleShowParamsDialog(record)" small>Parameters</v-chip>
                                        <v-chip class="mx-2" @click="handleQuote(record, index)" :disabled="quotes.includes(record)" small>Quote</v-chip>
                                    </v-list-item>
                                    <v-row v-if="index != (assetHashHistory.length - 1)" align="center" class="my-4 px-8">
                                        <v-divider></v-divider>
                                        <v-chip small class="justify-center text--secondary text-body-2" outlined>{{ getFriendlyDuration(record.created_at, assetHashHistory[index+1].created_at) }}</v-chip>
                                        <v-divider></v-divider>
                                    </v-row>                    
                                </div>
                            </template>
                        </v-list>
                        <div v-else class="text-center my-5">
                            <span class="text-h6 font-weight-light">Asset hash tracking is not enabled for <b>{{ collectionName }}</b></span>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-col>

            <v-col cols="5">
                <!-- <v-skeleton-loader class="mx-auto" max-width="480" type="list-item-avatar, list-item-three-line, image"></v-skeleton-loader>
                <v-skeleton-loader class="mx-auto" max-width="480" type="list-item-avatar, list-item-three-line, image"></v-skeleton-loader> -->
                <v-card v-if="drawerStatus == 'expanded'" v-sticky class="mx-auto">
                    <v-toolbar dark>
                        <v-toolbar-title>Comments</v-toolbar-title>
                        <v-spacer></v-spacer>
                    </v-toolbar>

                    <v-list>
                        <div class="scrollable" id="scrollable-comments" >
                            <template v-for="(comment, index) in comments">
                                <div :key="index">
                                    <v-list-item >                
                                        <v-row no-gutters>
                                            <v-col cols="1">
                                                <v-avatar class="mt-3 ml-1" size="32">
                                                    <amplify-s3-image class="avatar-small" :img-key="comment.user.profile ? comment.user.profile : 'default-user-profile-picture.png'" />
                                                </v-avatar>
                                            </v-col>
                                            <v-col class="pl-4" cols="11">
                                                <v-list-item-content>
                                                    <v-list-item-title class="font-weight-medium">{{ comment.user.name }}</v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        <template v-if="comment.user.company_name">{{ comment.user.company_name }}<span class="font-weight-bold">&nbsp;&middot;&nbsp;</span></template>
                                                        {{ relativeTime(comment.created_at) }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-content>{{ comment.text_content }}</v-list-item-content>
                                                </v-list-item-content>
                                            </v-col>
                                        </v-row>        
                                    </v-list-item>
                                    <v-list-item v-if="comment.key_traces.length > 0">
                                        <v-row no-gutters>
                                            <template v-for="(traceRecord, index) in comment.key_traces">
                                                <v-expand-transition :key="index">
                                                    <v-card class="pl-14" flat tile>
                                                        <v-row no-gutters>
                                                            <v-col cols="1">
                                                                <v-icon large>mdi-format-quote-open</v-icon>
                                                            </v-col>
                                                            <v-col class="py-0" cols="11">
                                                                <v-list-item class="px-0">
                                                                    <v-list-item-avatar tile size="60" color="grey darken-2">
                                                                        <span class="mx-auto text-h3 font-weight-medium white--text text-uppercase">{{ traceRecord.msp_id.charAt(0) }}</span>
                                                                    </v-list-item-avatar>
                                                                    <v-list-item-content class="quoted">
                                                                        <div class="overline timestamp mb-0">{{ toLongFormatWithYearAndMilliSec(traceRecord.created_at) }}</div>
                                                                        <v-list-item-title v-if="traceRecord.operation_type == KEY_TRACE_OPERATION_TYPE_READ" class="headline mb-1">READ by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-title v-if="creationId == traceRecord.id" class="headline mb-1">CREATED by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-title v-if="creationId != traceRecord.id && traceRecord.operation_type == KEY_TRACE_OPERATION_TYPE_WRITE" class="headline mb-1">UPDATED by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-title v-if="traceRecord.operation_type == KEY_TRACE_OPERATION_TYPE_DELETE" class="headline mb-1">DELETED by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-subtitle>via <a>{{ traceRecord.web_request.app_name }}<v-icon size="20" color="primary">mdi-open-in-new</v-icon></a></v-list-item-subtitle>
                                                                    </v-list-item-content>
                                                                </v-list-item>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card>
                                                </v-expand-transition>
                                            </template>                                            
                                        </v-row>
                                    </v-list-item>                                
                                    <v-divider inset></v-divider>
                                </div>
                            </template>
                        </div>
                        <!-- New comment box -->
                        <v-list-item class="mt-5" id="startOfCommentBox">
                            <v-row>
                                <v-col cols="1">
                                    <v-avatar class="mt-3 ml-1" size="32">
                                        <amplify-s3-image class="avatar-small" :img-key="currentUserProfilePictureURL() ? currentUserProfilePictureURL() : 'default-user-profile-picture.png'" />
                                    </v-avatar>
                                </v-col>
                                <v-col cols="11">
                                    <v-form ref="newCommentForm" class="pl-2" @submit.prevent="handlePostComment">
                                        <v-textarea @focus="showCommentBoxActions = true" name="comment" @input="comment = $event" class="pt-2" placeholder="Add a comment" filled rounded dense auto-grow rows="1"></v-textarea>       
                                        <v-row no-gutters>
                                            <template v-for="(traceRecord, index) in quotes">
                                                <v-expand-transition :key="index">
                                                    <v-card flat tile>
                                                        <v-row no-gutters>
                                                            <v-col cols="1">
                                                                <v-icon large>mdi-format-quote-open</v-icon>
                                                            </v-col>
                                                            <v-col class="py-0" cols="10">
                                                                <v-list-item class="px-0">
                                                                    <v-list-item-avatar tile size="60" color="grey darken-2">
                                                                        <span class="mx-auto text-h3 font-weight-medium white--text text-uppercase">{{ traceRecord.msp_id.charAt(0) }}</span>
                                                                    </v-list-item-avatar>
                                                                    <v-list-item-content class="quoted">
                                                                        <div class="overline timestamp mb-0">{{ toLongFormatWithYearAndMilliSec(traceRecord.created_at) }}</div>
                                                                        <v-list-item-title v-if="traceRecord.operation_type == KEY_TRACE_OPERATION_TYPE_READ" class="headline mb-1">READ by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-title v-if="creationId == traceRecord.id" class="headline mb-1">CREATED by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-title v-if="creationId != traceRecord.id && traceRecord.operation_type == KEY_TRACE_OPERATION_TYPE_WRITE" class="headline mb-1">UPDATED by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-title v-if="traceRecord.operation_type == KEY_TRACE_OPERATION_TYPE_DELETE" class="headline mb-1">DELETED by {{ traceRecord.msp_id }}</v-list-item-title>
                                                                        <v-list-item-subtitle>via <a>{{ traceRecord.app_name }}<v-icon size="20" color="primary">mdi-open-in-new</v-icon></a></v-list-item-subtitle>
                                                                    </v-list-item-content>
                                                                </v-list-item>
                                                            </v-col>
                                                            <v-col cols="1">
                                                                <v-icon @click="handleUnquote(index)" class="mt-10" >mdi-trash-can-outline</v-icon>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card>
                                                </v-expand-transition>
                                            </template>                                            
                                        </v-row>
                                        <v-expand-transition>
                                            <v-row class="mt-5" v-show="showCommentBoxActions" no-gutters align="center" justify="space-between">                       
                                                <v-btn :disabled="loading" class="white--text" color="blue-grey">
                                                    <v-icon left dark>mdi-camera</v-icon>
                                                    Add a photo or video
                                                </v-btn>                            
                                                <v-btn type="submit" :loading="loading" :disabled="loading" color="primary">Comment</v-btn>                            
                                            </v-row>
                                        </v-expand-transition>
                                    </v-form>
                                </v-col>
                            </v-row>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import BusinessTransaction from '@/components/BusinessTransaction.vue'
    import VueJsonPretty from 'vue-json-pretty'
    import VueJsonCompare from 'vue-json-compare'
    import Api from '@/services/api'
    import TimeHelper from '@/helpers/time_helper'
    import StringHelper from '@/helpers/string_helper'
    import Constants from '@/config/constants'

    export default {
        name: 'AssetView',
        components: {
            BusinessTransaction, VueJsonPretty, VueJsonCompare
        },
        props: {
            assetKey: String,
            drawerStatus: String,
            headerBgColor: String,
            headerTextColor: String
        },
        watch: {
            assetKey: function (newVal, oldVal) {
                this.quotes = []
                this.comment = null
                this.loading = false
                this.showCommentBoxActions = false
                this.assetViewTab = 0
                this.newBusinessTxnName = null
                this.newBusinessTxnMilestones = []                
                this.showSuccessAlert = false
                this.bizTxns = []
                Api.assets.getAssetOperationHistory(
                    {
                        asset_key: newVal
                    }                    
                )
                .then((response) => {
                    this.assetOperationHistory = response.data;
                    // Find distinct tracked hash value but have to filter out empty tracked hash value 
                    let nonNullTrackedHashValue = this.assetOperationHistory.filter(function(item) {
                        if (!item['tracked_hash_value']) {
                            return false
                        } else {
                            return true
                        }
                    })
                    this.assetHashHistory = [...new Map(nonNullTrackedHashValue.map((item) => {
                        return [item['tracked_hash_value'], item]
                    })).values()];
                    console.log("Asset operation history", this.assetOperationHistory);
                    console.log("Asset hash history", this.assetHashHistory);
                    this.getTxnDefs();
                })
                Api.assets.getAssetURIHistory(
                    {
                        asset_key: newVal
                    }                    
                )
                .then((response) => {
                    console.log("Asset URI history", response.data);
                    this.assetURIHistory = response.data;
                })
                Api.assetComments.getComments(
                    {
                        asset_key: newVal
                    }                    
                )
                .then((response) => {
                    console.log("Comments", response.data)
                    this.comments = response.data
                    setTimeout(() => {
                        var container = this.$el.querySelector("#scrollable-comments");
                        container.scrollTop = container.scrollHeight;                   
                    }, 500)
                })
            },
        },
        mounted: function() {
        },
        created () {
            this.KEY_TRACE_OPERATION_TYPE_READ = Constants.KEY_TRACE_OPERATION_TYPE_READ;
            this.KEY_TRACE_OPERATION_TYPE_WRITE = Constants.KEY_TRACE_OPERATION_TYPE_WRITE;
            this.KEY_TRACE_OPERATION_TYPE_DELETE = Constants.KEY_TRACE_OPERATION_TYPE_DELETE;
        },
        computed: {
            ...mapGetters([
                'contrastTimePeriodStart',
                'contrastTimePeriodEnd',            
            ]),
            cssVars () {
                return  {
                    /* variables you want to pass to css */
                    '--headerBgColor': this.headerBgColor ? this.headerBgColor : 'white',
                    '--headerTextColor': this.headerTextColor ? this.headerTextColor : 'rgba(0, 0, 0, 0.6)'
                }            
            },
            ...mapGetters([
                'getContrastTimeSelection',
            ]),
            channelName: function() {
                return this.assetOperationHistory[0].channel_name
            },
            collectionName: function() {
                return this.assetOperationHistory[0].collection
            },         
            lastUpdateTime: function() {
                let writeTimes = this.assetOperationHistory.map( (trace) => { return trace.operation_type == Constants.KEY_TRACE_OPERATION_TYPE_WRITE ? trace.written_at : null } )
                return writeTimes.nonNull().first()
            },
            creationInfo: function() { // return [trace.id, trace.written_at, trace.web_txn_id]
                let writeTimes = this.assetOperationHistory.map( (trace) => { return trace.operation_type == Constants.KEY_TRACE_OPERATION_TYPE_WRITE ? [trace.id, trace.written_at, trace.web_txn_id] : null })
                let writeTimesWithoutNull = writeTimes.nonNull()                
                if (writeTimesWithoutNull.length == 0) {
                    return null
                } else {
                    return writeTimesWithoutNull[writeTimesWithoutNull.length - 1]
                }
            },
            creationId: function() {
                const info = this.creationInfo
                return info ? info[0] : null
            },
            creationTime: function() {
                const info = this.creationInfo
                return info ? info[1] : null
            },
            creationWebTxnId: function() {
                const info = this.creationInfo
                return info ? info[2] : null
            },
            sortedNewBusinessTxnMilestones: function() {
                return [...this.newBusinessTxnMilestones].sort(function(a,b){
                    // Turn your strings into dates, and then subtract them
                    // to get a value that is either negative, positive, or zero.
                    return new Date(b.start_time) - new Date(a.start_time);
                });         
            },
            hashLabel: function() {
                return (this.assetOperationHistory.length > 0) ? this.assetOperationHistory[0].tracked_hash_label : ''         
            }

        },
        methods: {
            ...TimeHelper,
            ...StringHelper,

            handleSubmit: function() {
                if (!this.$refs.newBizTxnForm.validate()) {
                    return
                }
                let msp_uris = this.newBusinessTxnMilestones.map( (ele) => { return { msp_id: ele.msp_id, uri: ele.uri }})
                Api.bizTxn.create(
                    {                         
                        name: this.newBusinessTxnName,
                        channel_name: this.channelName,
                        msp_uris: msp_uris,
                    }                    
                )
                .then((response) => {
                    console.log("Response from bizTxn.create", response.data)
                    this.$refs.newBizTxnForm.reset()
                    let defId = response.data[0].definition_id
                    this.getMilestonesByTxnDefId(defId)
                    this.showBusinessTransactionDialog = false;
                    this.$refs.newBizTxnForm.reset();                    
                    this.newBusinessTxnName = null
                    this.newBusinessTxnMilestones = []
                    this.newBusinessTxnContrastTime = 0
                    this.showSuccessAlert = true
                    setTimeout(() => { this.showSuccessAlert = false }, 10000)
                })
            },

            handleRemoveMilestone: function(record, index) {
                console.log("Removing milestone...", record);
                const targetIndex =  this.newBusinessTxnMilestones.indexOf(record)
                this.newBusinessTxnMilestones.splice(targetIndex, 1)
                console.log("Milestones", this.newBusinessTxnMilestones)
                let msp_uris = this.sortedNewBusinessTxnMilestones.map( (ele) => { return { msp_id: ele.msp_id, uri: ele.uri  }})
                console.log("Getting vital data for these msp and uri", msp_uris)
                if (msp_uris.length > 1) { // No leg unless there are more than one milestone
                    Api.bizTxn.getVitalByMSPAndURIs(
                        {                            
                            msp_uris: msp_uris,
                            time_period_start: this.contrastTimePeriodStart,
                            time_period_end: this.contrastTimePeriodEnd
                        }                    
                    )
                    .then((response) => {
                        console.log("Response from getVitalByMSPAndURIs", response.data)
                        this.newBusinessTxnContrastTime = response.data.avg_biz_txn_time
                    })
                }
            },

            handleAddMilestone: function(record, index) {
                console.log("Adding milestone...", record);
                this.newBusinessTxnMilestones.push( record )
                console.log("Milestones", this.newBusinessTxnMilestones)
                let msp_uris = this.sortedNewBusinessTxnMilestones.map( (ele) => { return { msp_id: ele.msp_id, uri: ele.uri  }})
                console.log("Getting vital data for these msp and uri", msp_uris)
                if (msp_uris.length > 1) { // No leg unless there are more than one milestone
                    Api.bizTxn.getVitalByMSPAndURIs(
                        {                            
                            msp_uris: msp_uris,
                            time_period_start: this.contrastTimePeriodStart,
                            time_period_end: this.contrastTimePeriodEnd
                        }                    
                    )
                    .then((response) => {
                        console.log("Response from getVitalByMSPAndURIs", response.data)
                        this.newBusinessTxnContrastTime = response.data.avg_biz_txn_time
                    })
                }
            },

            handlePostComment: function() {
                this.loading = true
                Api.assetComments.postComment(
                    {
                        asset_key: this.assetKey,
                        comment: this.comment,
                        quotes: this.quotes.map((el) => {return el.id})
                    }                    
                )
                .then((response) => {
                    console.log("Response from postComment", response.data)
                    this.$refs.newCommentForm.reset();
                    this.loading = false
                    this.comment = null
                    this.quotes = []
                    this.showCommentBoxActions = false
                    this.comments.push( response.data )
                    setTimeout(() => {
                        var container = this.$el.querySelector("#scrollable-comments");
                        container.scrollTop = container.scrollHeight;                  
                    }, 500)
                })
            },

            handleShowParamsDialog: function(traceRecord) {
                this.paramsValue = traceRecord.value_content;
                this.paramsDialog = true
            },

            handleQuote: function(traceRecord, index) {
                console.log("Quote this - ", traceRecord, index)
                this.quotes.push( traceRecord )
                this.showCommentBoxActions = true
                // this.$vuetify.goTo('#startOfCommentBox', { container: '.drawer', duration: 500, easing: 'easeInOutCubic' })
            },

            handleUnquote: function(index) {
                this.quotes.splice(index, 1);
            },

            getTxnDefs: function() {
                Api.bizTxn.getTxnDefsByChannelName(
                    {
                        channel_name: this.channelName
                    }
                )
                .then((response) =>{
                    console.log("Response from getTxnDefsByChannelName", response.data)
                    let defIds = response.data
                    for (let index=0; index < defIds.length; index++) {
                        let defId = defIds[index].definition_id
                        this.getMilestonesByTxnDefId(defId)
                    }
                })
            },

            getMilestonesByTxnDefId: function(txnDefId) {
                Api.bizTxn.getMilestonesByAssetKeyAndTxnDef(
                    {
                        asset_key: this.assetKey,
                        txn_def_id: txnDefId
                    }
                )
                .then((response) => {
                    console.log("Reponse from getMilestonesByAssetKeyAndTxnDef", response.data)
                    let index = this.bizTxns.push( response.data ) - 1
                    let msp_uris = this.bizTxns[index].milestones.map( (ele) => { return { msp_id: ele.msp_id, uri: ele.uri  }})
                    console.log("Getting vital data for these msp and uri", msp_uris)
                    Api.bizTxn.getVitalByMSPAndURIs(
                        {                            
                            msp_uris: msp_uris,
                            time_period_start: this.contrastTimePeriodStart,
                            time_period_end: this.contrastTimePeriodEnd
                        }                    
                    )
                    .then((response1) => {
                        console.log("Response from getVitalByMSPAndURIs", response1.data)
                        let newValue = this.bizTxns[index]
                        newValue.contrastTime = response1.data.avg_biz_txn_time
                        this.$set(this.bizTxns, index, newValue)
                    })
                }) 
            },

            hasOperation: function(operations, operationType) {
                return operations.split(',').includes(operationType.toString());
            }
        },
        data: () => ({
            assetOperationHistory: [],
            assetURIHistory: [],
            comment: "",
            loading: false,
            showCommentBoxActions: false,
            comments: [],
            quotes: [],
            assetViewTab: 0,
            showBusinessTransactionDialog: false,
            rules: {
                required: value => !!value || 'Required.'
            },
            newBusinessTxnName: null,
            newBusinessTxnMilestones: [],
            newBusinessTxnContrastTime: 0,
            bizTxns: [],
            showSuccessAlert: false,
            paramsDialog: false,
            paramsValue: null,
            assetHashHistory: [],
        }),
    }
</script>

<style scoped lang="scss">

    amplify-s3-image.avatar-small {
        --height: 32px;
        --width: 32px;
    }

    .scrollable {
        overflow-y: auto;
        height: 400px;
    }

    .quoted .timestamp {
        font-size: 12px !important;
    }

    .quoted .headline {
        font-size: 18px !important;
    }

    .quoted .v-list-item__subtitle {
        font-size: 12px !important;
    }

    .header-bg-color {
        background-color: var(--headerBgColor);
    }

    .header-text-color {
        color: var(--headerTextColor);
    }

</style>